/**
 * After a deploy, a tab that was open before it still points at file names that no longer exist.
 * Opening a page then fails to load its script. Reloading once fetches the new index.html and the
 * files it names. A second failure soon after is a real problem, so it is left to surface.
 */
const RELOAD_MARK = "memoru:chunk-reload-at";
/** A reload that fails again within this time is not tried again. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

interface Deps {
  storage?: Pick<Storage, "getItem" | "setItem">;
  reload?: () => void;
  now?: () => number;
}

/** Returns true when it started a reload, which means the error is handled. */
export function reloadOnceForStaleChunk({ storage, reload = () => window.location.reload(), now = Date.now }: Deps = {}): boolean {
  let store: Deps["storage"];
  try {
    store = storage ?? window.sessionStorage;
  } catch {
    // Storage can be blocked. Without a mark a reload could loop, so do not reload.
    return false;
  }
  try {
    const last = Number(store.getItem(RELOAD_MARK));
    if (Number.isFinite(last) && last > 0 && now() - last < CHUNK_RELOAD_WINDOW_MS) return false;
    store.setItem(RELOAD_MARK, String(now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Vite fires this event when a lazy chunk or its CSS cannot be loaded. */
export function installChunkReloadHandler(target: Pick<Window, "addEventListener"> = window, reload?: () => void) {
  target.addEventListener("vite:preloadError", (event) => {
    // Cancelling the event keeps the failed import from throwing while the page reloads.
    if (reloadOnceForStaleChunk({ reload })) event.preventDefault();
  });
}
